/**
 * True on a WebKit engine: Safari on macOS and iPadOS, and every browser on iOS (they all run
 * WebKit, so their user agent has `AppleWebKit` and no `Chrome/` token; Chrome on iOS says `CriOS`).
 *
 * WebRTC needs this because WebKit never delivers media from a receiver that a remote offer creates
 * after we rolled back our own pending offer (AVS-4484), and Chromium does not share the defect.
 * Zero imports, like the other module-level helpers the VOIP core depends on.
 */
export function isWebKitEngine(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): boolean {
  return /AppleWebKit/.test(userAgent) && !/Chrome\//.test(userAgent);
}

type TTrackLike = { kind?: unknown; id?: unknown; readyState?: unknown; muted?: unknown };

function isTrackLike(value: unknown): value is TTrackLike {
  return typeof value === 'object' && value !== null;
}

type TKinds = { audio: boolean; video: boolean };

/** The ids of the muted receiver tracks of each media kind. */
export interface IMutedReceiverTracks {
  audio: ReadonlySet<string>;
  video: ReadonlySet<string>;
}

/**
 * The ids of the live remote tracks whose `muted` flag is true, per kind (AVS-4484). WebKit only: a
 * healthy WebKit receiver is never muted while media flows, but Chromium reports `muted:true` on tracks
 * that paint fine after a rollback, so there it would fire on healthy links. A stream without
 * `getTracks` (a mock, a foreign object) reads as "nothing muted".
 *
 * Ids, not kinds: a peer can hold several receivers of one kind, and the counters of a healthy one
 * must not vouch for a muted one.
 */
export function mutedReceiverTrackIds(stream: unknown): IMutedReceiverTracks {
  const ids = { audio: new Set<string>(), video: new Set<string>() };

  if (!isWebKitEngine() || typeof stream !== 'object' || stream === null || !('getTracks' in stream)) {
    return ids;
  }

  const getTracks: unknown = stream.getTracks;

  if (typeof getTracks !== 'function') {
    return ids;
  }

  const result: unknown = getTracks.call(stream);

  if (!Array.isArray(result)) {
    return ids;
  }

  const tracks: readonly unknown[] = result;

  for (const track of tracks) {
    if (!isTrackLike(track) || typeof track.id !== 'string' || track.readyState === 'ended' || !track.muted) {
      continue;
    }

    if (track.kind === 'audio' || track.kind === 'video') {
      ids[track.kind].add(track.id);
    }
  }

  return ids;
}

/**
 * The inbound counter of every attributable receiver: `trackIdentifier` (equal to `receiver.track.id`
 * in WebKit and Chromium, one inbound-rtp entry per receiver) mapped to `framesDecoded` for video or
 * `bytesReceived` for audio. An entry with no string `trackIdentifier` cannot be matched to a track
 * and is left out, so a muted track nobody can vouch for never reads as flowing.
 */
export function inboundCountersByTrack(entries: Iterable<unknown>): Map<string, number> {
  const counters = new Map<string, number>();

  for (const entry of entries) {
    if (!isStatLike(entry) || entry.type !== 'inbound-rtp' || typeof entry.trackIdentifier !== 'string') {
      continue;
    }

    if (entry.kind === 'video') counters.set(entry.trackIdentifier, Number(entry.framesDecoded) || 0);
    if (entry.kind === 'audio') counters.set(entry.trackIdentifier, Number(entry.bytesReceived) || 0);
  }

  return counters;
}

type TStatLike = {
  type?: unknown;
  kind?: unknown;
  trackIdentifier?: unknown;
  framesDecoded?: unknown;
  bytesReceived?: unknown;
};

function isStatLike(value: unknown): value is TStatLike {
  return typeof value === 'object' && value !== null;
}

/** The kinds that have a muted track with an inbound entry in `current`: media is arriving for it. */
export function reportedMutedKinds(muted: IMutedReceiverTracks, current: ReadonlyMap<string, number>): TKinds {
  const reported = (ids: ReadonlySet<string>) => [...ids].some((id) => current.has(id));

  return { audio: reported(muted.audio), video: reported(muted.video) };
}

/**
 * The kinds that have a muted track whose own counter is higher in `current` than in `previous`. A
 * track with no entry in `previous` counts from zero: a receiver a remote offer just created has no
 * inbound-rtp entry until its first RTP arrives, which is after the baseline read.
 */
export function risingMutedKinds(
  muted: IMutedReceiverTracks,
  previous: ReadonlyMap<string, number>,
  current: ReadonlyMap<string, number>
): TKinds {
  const rising = (ids: ReadonlySet<string>) =>
    [...ids].some((id) => {
      const now = current.get(id);

      return now !== undefined && now > (previous.get(id) ?? 0);
    });

  return { audio: rising(muted.audio), video: rising(muted.video) };
}
